import {
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material"
import type { FC } from "react"
import { twMerge } from "tailwind-merge"

import {
  InventoryLotTableHeaderCells,
  InventoryLotTableRowCells,
  InventoryPalletTableHeaderCells,
  InventoryPalletTableRowCells,
} from "../../../../components"
import {
  ALL_LOTS_LOCKED_TOOLTIP,
  LOT_COLUMN_COUNT,
  LOT_LOCKED_TOOLTIP,
  LOT_TOGGLE_COLUMN_COUNT,
  PALLET_COLUMN_COUNT,
  PALLET_LOCKED_TOOLTIP,
  PALLET_SEARCH_PLACEHOLDER,
  PALLET_TOGGLE_COLUMN_COUNT,
} from "../../../../constants"
import { isLotLocked } from "../../../../utils"
import { getLotRowBorderClassName } from "../../utils/transfer"

import SearchInput from "@/components/SearchInput/SearchInput"
import { AerosMultiSelect } from "@/components/UiKit/AerosMultiSelect"
import {
  type InventoryPalletGroup,
  type InventoryPalletGroupsSortField,
  type InventoryPalletLot,
  type SortingOrder,
  TransferType,
} from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"

interface TransferPalletSelectionProps {
  palletGroups: InventoryPalletGroup[]
  isFetching: boolean
  transferType: TransferType
  selectedLotIds: Set<string>
  sortBy: InventoryPalletGroupsSortField
  sortDirection: SortingOrder
  aisleCodes: string[]
  aisleOptions: Option<string>[]
  uomName: string
  onLotSelectionChange?: (pallet: InventoryPalletGroup, lot: InventoryPalletLot) => void
  onPalletSelectionChange: (pallet: InventoryPalletGroup) => void
  onSortChange: (field: InventoryPalletGroupsSortField, direction: SortingOrder) => void
  onSearchChange: (search: string) => void
  onAisleCodesChange: (aisleCodes: string[]) => void
}

export const TransferPalletSelection: FC<TransferPalletSelectionProps> = ({
  palletGroups,
  isFetching,
  transferType,
  selectedLotIds,
  sortBy,
  sortDirection,
  aisleCodes,
  aisleOptions,
  uomName,
  onLotSelectionChange,
  onPalletSelectionChange,
  onSortChange,
  onSearchChange,
  onAisleCodesChange,
}) => {
  const isLotSelectable = transferType === TransferType.InterWarehouse
  const columnCount =
    PALLET_TOGGLE_COLUMN_COUNT +
    PALLET_COLUMN_COUNT +
    (isLotSelectable ? LOT_TOGGLE_COLUMN_COUNT : 0) +
    LOT_COLUMN_COUNT

  return (
    <div className='flex min-h-0 flex-1 flex-col rounded-lg border border-gray-400 p-4'>
      <div className='mb-4 flex items-center justify-between gap-4'>
        <Typography variant='h6' className='flex-1 font-bold leading-10 text-gray-800'>
          1. Select Pallets
        </Typography>
        <div className='flex items-end justify-end gap-4'>
          <SearchInput
            placeholder={PALLET_SEARCH_PLACEHOLDER}
            onChange={(e) => onSearchChange(e.target.value)}
            iconPosition='end'
            className='w-[360px]'
            data-testid={`transfer.pallet-selection.search-field`}
          />
          <AerosMultiSelect
            label='Filter by Aisle'
            placeholder='Select one'
            options={aisleOptions}
            value={aisleCodes}
            onChange={onAisleCodesChange}
            withSelectAll
            className='w-[360px]'
            data-testid={`transfer.pallet-selection.aisle-field`}
          />
        </div>
      </div>
      <div className='relative flex min-h-0 flex-1'>
        {isFetching && (
          <div className='absolute inset-0 top-[53px] z-10 flex items-center justify-center bg-white/60'>
            <CircularProgress size={48} />
          </div>
        )}
        <TableContainer className='overflow-auto'>
          <Table stickyHeader>
            <TableHead className='bg-gray-100'>
              <TableRow>
                <TableCell className='w-12' />
                <InventoryPalletTableHeaderCells sort={{ sortBy, sortDirection, onSortChange }} />
                {isLotSelectable && <TableCell className='w-12' />}
                <InventoryLotTableHeaderCells sort={{ sortBy, sortDirection, onSortChange }} />
              </TableRow>
            </TableHead>

            <TableBody>
              {!palletGroups.length && (
                <TableRow>
                  <TableCell colSpan={columnCount} align='center' className='py-8'>
                    No pallets found
                  </TableCell>
                </TableRow>
              )}

              {palletGroups.map((pallet) => {
                // Inter-warehouse transfers select individual lots, so locked lots are skipped; intra-warehouse transfers move whole pallets.
                const palletLots = isLotSelectable ? pallet.lots.filter((lot) => !isLotLocked(lot)) : pallet.lots
                const selectedLotCount = palletLots.filter((lot) => selectedLotIds.has(lot.rawInventoryId)).length
                const isPalletDisabled = isLotSelectable
                  ? pallet.lots.every(isLotLocked)
                  : pallet.lots.some(isLotLocked)
                const isPalletSelected = !!palletLots.length && selectedLotCount === palletLots.length
                const isPalletIndeterminate = !!selectedLotCount && !isPalletSelected

                return pallet.lots.map((lot, lotIndex) => {
                  const isFirstLot = lotIndex === 0
                  const isLastLot = lotIndex === pallet.lots.length - 1
                  const isLotDisabled = isLotLocked(lot)
                  const isRowDisabled = isLotSelectable ? isLotDisabled : isPalletDisabled

                  return (
                    <TableRow
                      key={lot.rawInventoryId}
                      className={twMerge(isRowDisabled && "bg-gray-100", getLotRowBorderClassName(isLastLot))}
                      data-testid={`transfer.pallet-selection.row-${lot.rawInventoryId}`}
                    >
                      {isFirstLot ? (
                        <Tooltip
                          title={isLotSelectable ? ALL_LOTS_LOCKED_TOOLTIP : PALLET_LOCKED_TOOLTIP}
                          disableHoverListener={!isPalletDisabled}
                        >
                          <TableCell>
                            <Checkbox
                              size='small'
                              checked={isPalletSelected}
                              indeterminate={isPalletIndeterminate}
                              disabled={isPalletDisabled}
                              onChange={() => onPalletSelectionChange(pallet)}
                              data-testid={`transfer.pallet-selection.pallet-checkbox-${pallet.referenceNbr}`}
                            />
                          </TableCell>
                        </Tooltip>
                      ) : (
                        <TableCell className='h-16' />
                      )}
                      <InventoryPalletTableRowCells pallet={pallet} isFirstLot={isFirstLot} uomName={uomName} />
                      {isLotSelectable && (
                        <Tooltip title={LOT_LOCKED_TOOLTIP} disableHoverListener={!isLotDisabled}>
                          <TableCell>
                            <Checkbox
                              size='small'
                              checked={selectedLotIds.has(lot.rawInventoryId)}
                              disabled={isLotDisabled}
                              onChange={() => onLotSelectionChange?.(pallet, lot)}
                              data-testid={`transfer.pallet-selection.lot-checkbox-${lot.rawInventoryId}`}
                            />
                          </TableCell>
                        </Tooltip>
                      )}
                      <InventoryLotTableRowCells lot={lot} uomName={uomName} />
                    </TableRow>
                  )
                })
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  )
}
